import { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import type { CategoryKey } from '@worldcoo/shared/core';
import { Searcher } from '@worldcoo/shared/searcher';
import type { AppRouter } from '@/lib/trpc/router.server';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';

/**
 * The directory's view of a partner: one entry of the `portal.getPartnerNgos` payload.
 *
 * Inferred from the router rather than written out, because the wire type is not quite the domain
 * type — serialization turns every `T | undefined` field into an optional one — and a hand-written
 * copy drifts from the query the moment a field is added. Type-only import, so nothing from the
 * server module reaches the client bundle (same trick as `AppRouter` in `@/lib/trpc`).
 */
export type PartnerNgo = inferRouterOutputs<AppRouter>['portal']['getPartnerNgos']['ngos'][number];

/** One entry of the `portal.getPartnerNgoProjects` payload, inferred for the same reason. */
export type PartnerNgoProject = inferRouterOutputs<AppRouter>['portal']['getPartnerNgoProjects']['projects'][number];

export type NgoFilters = {
	query: string;
	country: string | null;
	region: string | null;
	category: CategoryKey | null;
};

export const EMPTY_FILTERS: NgoFilters = { query: '', country: null, region: null, category: null };

/**
 * Country code → name in the visitor's language, via `Intl.DisplayNames`.
 *
 * Preferred over the static `@worldcoo/monolith/forms/countries` table, which is English-only and
 * would print "Spain" on the Spanish page. Falls back to the raw code on the (unlikely) engine that
 * cannot name it, so a filter entry is never blank.
 */
function useCountryNamer(): (code: string) => string {
	const { language } = useTranslation();
	return useMemo(() => {
		const names = new Intl.DisplayNames([language], { type: 'region' });
		return (code: string) => {
			try {
				return names.of(code.toUpperCase()) ?? code;
			} catch {
				return code;
			}
		};
	}, [language]);
}

/** A filter dropdown's options: the distinct values actually present, named and sorted. */
export type FilterOption = { value: string; label: string };

function distinct(values: (string | undefined)[]): string[] {
	return [...new Set(values.filter((value): value is string => !!value))];
}

/**
 * The directory's data: the partner roster, the options each dropdown should offer, and the roster
 * narrowed by the filters currently applied.
 *
 * The dropdown options come from the FULL roster rather than the filtered one, so choosing a country
 * never empties the region list underneath the visitor's cursor. Filters therefore compose freely and
 * a combination that matches nothing lands on the empty state, which is what the hand-off draws.
 */
export function usePartnerNgos(filters: NgoFilters) {
	const { data } = useSuspenseQuery(trpc.portal.getPartnerNgos.queryOptions());
	const nameCountry = useCountryNamer();
	const ngos = data.ngos;

	const countryOptions = useMemo<FilterOption[]>(
		() =>
			distinct(ngos.map((ngo) => ngo.countryCode))
				.map((value) => ({ value, label: nameCountry(value) }))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[ngos, nameCountry],
	);

	const regionOptions = useMemo<FilterOption[]>(
		() =>
			distinct(ngos.map((ngo) => ngo.region))
				.map((value) => ({ value, label: value }))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[ngos],
	);

	const filtered = useMemo(() => {
		// Accent-insensitive, and every word has to appear somewhere in the name rather than the whole
		// string verbatim: 95 of the 133 names carry a diacritic, so a raw `includes` fails to find
		// "Acción Contra el Hambre" from "accion", and "Arrels Fundació" from "fundacio". `every` over
		// an empty term list is `true`, which is what leaves an empty query matching everything.
		const terms = Searcher.normalizeString(filters.query).split(/\s+/).filter(Boolean);
		return ngos.filter((ngo) => {
			const name = Searcher.normalizeString(ngo.name);
			if (!terms.every((term) => name.includes(term))) return false;
			if (filters.country && ngo.countryCode !== filters.country) return false;
			if (filters.region && ngo.region !== filters.region) return false;
			if (filters.category && !ngo.categories.includes(filters.category)) return false;
			return true;
		});
	}, [ngos, filters]);

	return { ngos, filtered, countryOptions, regionOptions };
}
